import React, { useCallback, useState } from 'react'
import { Button } from '@/atoms/Button'
import { GlassCircle } from '@/atoms/GlassCircle'
import { ShareOutlineIcon } from '@/atoms/Icons/ShareOutlineIcon'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { ShareMomentDialog } from '@/organisms/ShareMoment/ShareMomentDialog'
import { MomentUserStory } from '@/services/MomentsService/types'
import { Moment } from '@/types/codegen-federation'
import { PlayableStory } from '@/utils/StoryUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface ShareMomentProps {
  moment: Moment
  userId?: string
  iconSize?: number
  size?: string
  glass?: boolean
  story?: PlayableStory | MomentUserStory
  showIcon?: boolean
}

export const ShareMoment: React.FC<ShareMomentProps> = ({
  iconSize = 18,
  glass,
  moment,
  story,
  userId,
  size = 40,
  showIcon = true,
}) => {
  const { engagementMoment } = useCollectiblesPlayerEngagement()
  const { controller } = useAngelPlayerContext()
  const { t } = useTranslate('watch')
  const share = t('share', 'Share')
  const track = useSafeTrack()

  const [isShareOpen, setIsShareOpen] = useState(false)

  const openShareDialog = useCallback(() => {
    track('Share Dialog Opened', {
      momentId: engagementMoment?.id,
      episodeGuid: engagementMoment?.episode?.guid,
    })
    setIsShareOpen(true)
  }, [engagementMoment, track])

  const closeShareDialog = useCallback(() => {
    setIsShareOpen(false)
  }, [])

  return (
    <div>
      {glass ? (
        <GlassCircle
          size={size}
          onClick={() => {
            controller.exitFullscreen()
            openShareDialog()
          }}
        >
          {showIcon ? <ShareOutlineIcon size={iconSize} /> : share}
        </GlassCircle>
      ) : (
        <Button
          onClick={() => {
            controller.exitFullscreen()
            openShareDialog()
          }}
          variant="plain"
          className="text-md font-light"
        >
          {showIcon ? <ShareOutlineIcon size={iconSize} /> : share}
        </Button>
      )}
      {isShareOpen && (
        <ShareMomentDialog
          isOpen={isShareOpen}
          onClose={closeShareDialog}
          moment={moment}
          userId={userId}
          story={story}
        />
      )}
    </div>
  )
}
